import { FeatureFlagProvider, useCurrentFeatureFlags } from '@platform/feature-flags';
import { Alert, Box, Button } from '@shared/ui';
import { getProxiedAvatarUrl, getStoredAccessToken } from '@shared/utils/avatar';
import { buildImpersonationUser } from '@shared/utils/impersonation';
import { useStytchMemberSession } from '@stytch/react/b2b';
import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { tokenService } from '../../services/tokenService';
import {
  apiSlice,
  useGetGoogleConnectionStatusQuery,
  useGetMeQuery,
} from '../../store/api/apiSlice';
import { resetUserScopedApiState } from '../../store/api/resetUserScopedApiState';
import { rpcSlice } from '../../store/api/rpcSlice';
import {
  setAppUserId,
  setSession,
  setUserProfile,
  setUserResolved,
  startImpersonation,
} from '../../store/auth/authSlice';
import { selectEffectivePersonId } from '../../store/auth/selectors';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { clearStytchCookies } from './providers/clearStytchCookies';
import { useStytch } from './providers/StytchContext';
import { shouldRequireGoogleDriveForUser } from './userRoles';

interface AuthContextValue {
  isExchanging: boolean;
}

const AuthContext = createContext<AuthContextValue>({ isExchanging: false });

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: React.ReactNode;
}

const DRIVE_CONNECT_PENDING_TOKEN_KEY = 'drive_connect_pending_token';
const DRIVE_CONNECT_PENDING_SOURCE_KEY = 'drive_connect_pending_source';
const DRIVE_CONNECT_PENDING_STATE_KEY = 'drive_connect_state';
const GOOGLE_MIGRATION_PROMPT_DISMISSED_KEY = 'google_migration_prompt_dismissed';

const GOOGLE_PROVIDER_DISPLAY_ORDER = ['google-drive', 'google-calendar', 'google-gmail'] as const;
const USER_FACING_GOOGLE_MIGRATION_PROVIDERS = ['google-drive'] as const;

const GOOGLE_PROVIDER_DISPLAY_NAMES: Record<string, string> = {
  'google-drive': 'Drive',
  'google-calendar': 'Calendar',
  'google-gmail': 'Gmail',
};

export function buildGoogleMigrationPromptDismissedKey(identityKey: string): string {
  const trimmedIdentityKey = identityKey.trim();
  if (trimmedIdentityKey === '') {
    return GOOGLE_MIGRATION_PROMPT_DISMISSED_KEY;
  }

  return `${GOOGLE_MIGRATION_PROMPT_DISMISSED_KEY}:${trimmedIdentityKey}`;
}

function normalizePromptIdentityKey(value: string | null | undefined): string {
  return (value || '').trim();
}

function buildGoogleMigrationPromptSessionIdentity(args: {
  previousIdentityKey: string;
  previousToken: string;
  token: string | null;
  userID: string | null | undefined;
}): { identityKey: string; token: string } {
  const normalizedToken = normalizePromptIdentityKey(args.token);
  const normalizedUserID = normalizePromptIdentityKey(args.userID);

  if (normalizedToken === '') {
    return {
      identityKey: '',
      token: '',
    };
  }

  if (args.previousIdentityKey === '') {
    return {
      identityKey: normalizedUserID || normalizedToken,
      token: normalizedToken,
    };
  }

  if (args.previousToken !== '' && args.previousToken !== normalizedToken) {
    return {
      identityKey: normalizedUserID || normalizedToken,
      token: normalizedToken,
    };
  }

  return {
    identityKey: args.previousIdentityKey,
    token: normalizedToken,
  };
}

type DriveRetryContext =
  | {
      source: 'token';
      token: string;
    }
  | {
      source: 'cookie';
    };

export function sortGoogleMigrationProviders(legacyProviders: string[]): string[] {
  const normalized = Array.from(
    new Set(
      legacyProviders
        .map((provider) => provider.trim().toLowerCase())
        .filter((provider) => provider.length > 0)
    )
  );

  return normalized.sort((left, right) => {
    const leftIndex = GOOGLE_PROVIDER_DISPLAY_ORDER.indexOf(
      left as (typeof GOOGLE_PROVIDER_DISPLAY_ORDER)[number]
    );
    const rightIndex = GOOGLE_PROVIDER_DISPLAY_ORDER.indexOf(
      right as (typeof GOOGLE_PROVIDER_DISPLAY_ORDER)[number]
    );

    if (leftIndex === -1 && rightIndex === -1) {
      return left.localeCompare(right);
    }
    if (leftIndex === -1) {
      return 1;
    }
    if (rightIndex === -1) {
      return -1;
    }

    return leftIndex - rightIndex;
  });
}

export function buildGoogleMigrationPromptSignature(legacyProviders: string[]): string {
  return sortGoogleMigrationProviders(legacyProviders).join('|');
}

export function filterUserFacingGoogleMigrationProviders(legacyProviders: string[]): string[] {
  const userFacingProviders = new Set<string>(USER_FACING_GOOGLE_MIGRATION_PROVIDERS);
  return sortGoogleMigrationProviders(legacyProviders).filter((provider) =>
    userFacingProviders.has(provider)
  );
}

export function describeGoogleMigrationProviders(legacyProviders: string[]): string {
  const labels = sortGoogleMigrationProviders(legacyProviders).map(
    (provider) => GOOGLE_PROVIDER_DISPLAY_NAMES[provider] ?? provider
  );

  if (labels.length === 0) {
    return 'Google';
  }
  if (labels.length === 1) {
    return labels[0];
  }
  if (labels.length === 2) {
    return `${labels[0]} and ${labels[1]}`;
  }

  return `${labels.slice(0, -1).join(', ')}, and ${labels[labels.length - 1]}`;
}

export function buildDriveRetryContextFromSessionToken(
  stytchSessionToken: string
): DriveRetryContext {
  if (stytchSessionToken) {
    return {
      source: 'token',
      token: stytchSessionToken,
    };
  }

  return { source: 'cookie' };
}

export function buildDriveRetryContextFromStorage(
  storedToken: string | null,
  storedSource: string | null
): DriveRetryContext | null {
  if (storedSource === 'cookie') {
    return { source: 'cookie' };
  }

  if (storedSource === 'token' && storedToken) {
    return {
      source: 'token',
      token: storedToken,
    };
  }

  // Backward compatibility for pre-source storage format.
  if (!storedSource && storedToken) {
    return {
      source: 'token',
      token: storedToken,
    };
  }

  return null;
}

export function shouldMarkUserResolved(args: {
  token: string | null;
  isUserResolved: boolean;
  isMeUninitialized: boolean;
  isFetchingMe: boolean;
  isMeError: boolean;
  hasUserProfile: boolean;
}): boolean {
  const { token, isUserResolved, isMeUninitialized, isFetchingMe, isMeError, hasUserProfile } =
    args;
  if (!token || isUserResolved) {
    return false;
  }

  if (isMeError) {
    return hasUserProfile;
  }

  return !isMeUninitialized && !isFetchingMe;
}

export function shouldExpireUnresolvedSession(args: {
  token: string | null;
  isUserResolved: boolean;
  isMeUninitialized: boolean;
  isFetchingMe: boolean;
  isMeError: boolean;
  hasUserProfile: boolean;
}): boolean {
  const { token, isUserResolved, isMeUninitialized, isFetchingMe, isMeError, hasUserProfile } =
    args;

  if (!token || isUserResolved || !isMeError || hasUserProfile) {
    return false;
  }

  return !isMeUninitialized && !isFetchingMe;
}

export function shouldAttemptCookieBasedExchange(args: {
  stytchSessionToken: string;
  hasPlatformToken: boolean;
  authStatus: string;
  hasStytchSession: boolean;
}): boolean {
  const { stytchSessionToken, hasPlatformToken, authStatus, hasStytchSession } = args;

  if (!hasStytchSession) {
    return false;
  }

  return stytchSessionToken === '' && !hasPlatformToken && authStatus !== 'authenticated';
}

/**
 * Determines whether the auth provider should attempt a silent cookie-based
 * token exchange for cross-app SSO. This handles the scenario where the user
 * authenticated on the main Mento app (e.g. app.mento.co) and the browser
 * carries the stytch_session_jwt HttpOnly cookie on the shared parent domain
 * (.mento.co), but the Moments Stytch SDK has no local session state.
 *
 * Conditions:
 *  - Stytch SDK has finished initializing (isInitialized)
 *  - No prior silent exchange attempt this page load (not already attempted)
 *  - No other exchange currently in-flight (not exchange in progress)
 */
export function shouldAttemptSilentCrossAppExchange(args: {
  isInitialized: boolean;
  alreadyAttempted: boolean;
  exchangeInProgress: boolean;
}): boolean {
  return args.isInitialized && !args.alreadyAttempted && !args.exchangeInProgress;
}

// Helper to hydrate impersonation state after normal auth hydration
// This must be called AFTER hydrateFromStorage() to ensure proper sequencing
// IMPORTANT: This function must only be called ONCE per page load to prevent infinite loops
// IMPORTANT: Only call this if the user has a valid underlying auth session
export function hydrateImpersonationIfActive(
  dispatch: ReturnType<typeof useAppDispatch>,
  hasValidSession: boolean
): boolean {
  const impersonationState = tokenService.loadImpersonationState();
  if (!impersonationState?.active) {
    return false;
  }

  // SAFEGUARD 0: Avoid restoring persisted impersonation when no underlying session signal exists.
  // We intentionally do not clear runtime impersonation here because cookie availability can vary
  // across environments (e.g. HttpOnly or domain-scoped cookie visibility).
  // If this returns false, tokenService.resolveActiveToken() can still bootstrap from
  // persisted impersonation storage until normal auth/session signals are available.
  if (!hasValidSession) {
    return false;
  }

  // SAFEGUARD 1: Check if impersonation token is expired
  // If expired, clear the state instead of trying to use it
  if (impersonationState.expiresAt) {
    const expiresAt = new Date(impersonationState.expiresAt);
    if (expiresAt <= new Date()) {
      tokenService.clearRuntimeImpersonation();
      return false;
    }
  }

  // SAFEGUARD 2: Validate required fields exist
  if (!impersonationState.token || !impersonationState.targetId) {
    console.warn('Invalid impersonation state (missing token or targetId), clearing');
    tokenService.clearRuntimeImpersonation();
    return false;
  }

  // Type-check the originalUser from storage to satisfy TypeScript
  const originalUser = impersonationState.originalUser as {
    id: string;
    email: string;
    organizationId: string;
    roles: string[];
    permissions: string[];
    firstName?: string;
    lastName?: string;
    avatarUrl?: string;
  } | null;

  // SAFEGUARD 3: Ensure we have original credentials to restore
  if (!impersonationState.originalToken || !originalUser) {
    console.warn('Invalid impersonation state (missing original credentials), clearing');
    tokenService.clearRuntimeImpersonation();
    return false;
  }

  if (!impersonationState.targetRoles || !impersonationState.targetPermissions) {
    console.warn('Invalid impersonation state (missing target roles or permissions), clearing');
    tokenService.clearRuntimeImpersonation();
    return false;
  }
  const targetRoles = impersonationState.targetRoles;
  const targetPermissions = impersonationState.targetPermissions;

  // Set the impersonation tracking state
  dispatch(
    startImpersonation({
      targetId: impersonationState.targetId,
      targetName: impersonationState.targetName,
      targetEmail: impersonationState.targetEmail,
      targetOrgId: impersonationState.targetOrgId || '',
      targetRoles,
      targetPermissions,
      returnPath: impersonationState.returnPath,
      token: impersonationState.token,
      expiresAt: impersonationState.expiresAt,
      originalToken: impersonationState.originalToken,
      originalUser,
    })
  );

  // CRITICAL: Swap auth.token and auth.user to impersonation values
  // Without this, the admin session from hydrateFromStorage remains active
  dispatch(
    setSession({
      token: impersonationState.token,
      user: buildImpersonationUser({
        id: impersonationState.targetId,
        email: impersonationState.targetEmail,
        name: impersonationState.targetName,
        orgId: impersonationState.targetOrgId || '',
        roles: targetRoles,
        permissions: targetPermissions,
      }),
    })
  );

  // Reset caches to ensure fresh data fetch as impersonated user
  dispatch(apiSlice.util.resetApiState());
  dispatch(rpcSlice.util.resetApiState());

  return true;
}

// Inner component that uses Stytch hooks - only rendered when provider is ready
function AuthProviderInner({ children }: AuthProviderProps) {
  const memberSession = useStytchMemberSession();
  const dispatch = useAppDispatch();
  const { session, isInitialized } = memberSession;
  const token = useAppSelector((s) => s.auth.token);
  const effectivePersonId = useAppSelector(selectEffectivePersonId);
  const status = useAppSelector((s) => s.auth.status);
  const isUserResolved = useAppSelector((s) => s.auth.isUserResolved);
  const user = useAppSelector((s) => s.auth.user);
  const {
    currentData: meData,
    isFetching: isFetchingMe,
    isUninitialized: isMeUninitialized,
    isError: isMeError,
  } = useGetMeQuery(effectivePersonId, {
    skip: !token || !effectivePersonId,
  });
  const currentFeatureFlags = useCurrentFeatureFlags();
  const { data: googleStatus } = useGetGoogleConnectionStatusQuery(undefined, {
    refetchOnFocus: true,
    refetchOnReconnect: true,
    skip: !token || status !== 'authenticated' || !isUserResolved,
  });
  const { stytch } = useStytch();
  const [isExchanging, setIsExchanging] = useState(false);
  const previousEffectivePersonIdRef = useRef<string | null>(effectivePersonId);
  useEffect(() => {
    const previousPersonId = previousEffectivePersonIdRef.current;
    if (previousPersonId !== null && previousPersonId !== effectivePersonId) {
      resetUserScopedApiState(dispatch);
    }
    previousEffectivePersonIdRef.current = effectivePersonId;
  }, [dispatch, effectivePersonId]);

  const googleMigrationPromptIdentityRef = useRef('');
  const googleMigrationPromptTokenRef = useRef('');
  const googleMigrationPromptIdentity = buildGoogleMigrationPromptSessionIdentity({
    previousIdentityKey: googleMigrationPromptIdentityRef.current,
    previousToken: googleMigrationPromptTokenRef.current,
    token,
    userID: typeof user?.id === 'string' ? user.id : null,
  });
  googleMigrationPromptIdentityRef.current = googleMigrationPromptIdentity.identityKey;
  googleMigrationPromptTokenRef.current = googleMigrationPromptIdentity.token;
  const googleMigrationPromptDismissedKey = buildGoogleMigrationPromptDismissedKey(
    googleMigrationPromptIdentity.identityKey
  );
  const [dismissedGoogleMigrationPrompt, setDismissedGoogleMigrationPrompt] = useState<
    string | null
  >(null);
  const exchangeRunRef = useRef(0);
  const pendingDriveRetryRef = useRef<DriveRetryContext | null>(null);
  // Track the last Stytch session token we successfully exchanged to avoid re-exchanging on routine refreshes
  const lastExchangedStytchTokenRef = useRef<string | null>(null);
  // Mutex to prevent concurrent exchange attempts
  const exchangeInProgressRef = useRef(false);
  const pendingRetryRef = useRef(false);
  // Track if we've already hydrated impersonation state to prevent infinite loops.
  // CRITICAL: hydrateImpersonationIfActive() dispatches setSession() which changes `token`,
  // which is a dependency of the main useEffect. Without this guard, we'd get:
  // useEffect runs → hydrateImpersonation → setSession(token) → token changes → useEffect runs → ∞
  const impersonationHydratedRef = useRef(false);
  // Track whether we've attempted a silent cross-app cookie exchange.
  // This prevents repeated attempts on every effect re-run. The exchange
  // handles the case where the user authenticated on the main Mento app
  // (e.g. app.mento.co) and the browser carries the stytch_session_jwt
  // cookie on the shared parent domain (.mento.co), but the Moments Stytch
  // SDK has no local session state.
  const silentCrossAppExchangeAttemptedRef = useRef(false);
  const lastAttemptRef = useRef<{
    token: string | null;
    outcome: 'success' | 'failure' | null;
    timestamp: number;
  }>({
    token: null,
    outcome: null,
    timestamp: 0,
  });
  const legacyGoogleProviders = filterUserFacingGoogleMigrationProviders(
    googleStatus?.legacy_providers || []
  );
  const googleMigrationPromptSignature = buildGoogleMigrationPromptSignature(legacyGoogleProviders);
  const showGoogleMigrationPrompt =
    !!token &&
    status === 'authenticated' &&
    isUserResolved &&
    shouldRequireGoogleDriveForUser(user) &&
    !!googleStatus?.migration_required &&
    legacyGoogleProviders.length > 0 &&
    googleMigrationPromptSignature !== dismissedGoogleMigrationPrompt;

  useEffect(() => {
    if (typeof window === 'undefined') {
      setDismissedGoogleMigrationPrompt(null);
      return;
    }

    setDismissedGoogleMigrationPrompt(sessionStorage.getItem(googleMigrationPromptDismissedKey));
  }, [googleMigrationPromptDismissedKey]);

  const clearDriveState = useCallback(() => {
    pendingDriveRetryRef.current = null;
    sessionStorage.removeItem(DRIVE_CONNECT_PENDING_TOKEN_KEY);
    sessionStorage.removeItem(DRIVE_CONNECT_PENDING_SOURCE_KEY);
    sessionStorage.removeItem(DRIVE_CONNECT_PENDING_STATE_KEY);
  }, []);

  const persistDriveRetryState = useCallback(
    (retryContext: DriveRetryContext, state: string | undefined) => {
      pendingDriveRetryRef.current = retryContext;
      if (retryContext.source === 'token') {
        sessionStorage.setItem(DRIVE_CONNECT_PENDING_TOKEN_KEY, retryContext.token);
      } else {
        sessionStorage.removeItem(DRIVE_CONNECT_PENDING_TOKEN_KEY);
      }

      sessionStorage.setItem(DRIVE_CONNECT_PENDING_SOURCE_KEY, retryContext.source);
      if (state) {
        sessionStorage.setItem(DRIVE_CONNECT_PENDING_STATE_KEY, state);
      } else {
        sessionStorage.removeItem(DRIVE_CONNECT_PENDING_STATE_KEY);
      }
    },
    []
  );

  const handleDriveRetry = useCallback(async () => {
    const retryContext = pendingDriveRetryRef.current;
    if (!retryContext) {
      // No token to retry - clear state and let user sign in again
      clearDriveState();
      try {
        await stytch?.session?.revoke?.();
      } catch {}
      clearStytchCookies();
      tokenService.clearTokens();
      return;
    }

    try {
      const jwt =
        retryContext.source === 'token'
          ? await tokenService.exchangeStytchSession(retryContext.token)
          : await tokenService.exchangeStytchSessionFromCookie();
      if (jwt) {
        clearDriveState();
        return;
      }
      const prompt = tokenService.consumeDrivePrompt();
      if (prompt?.authUrl) {
        // Still need Drive consent - auto-redirect again
        persistDriveRetryState(retryContext, prompt.state);
        window.location.href = prompt.authUrl;
        return;
      }
      // No prompt and no JWT - something went wrong, sign out
      clearDriveState();
      try {
        await stytch?.session?.revoke?.();
      } catch {}
      clearStytchCookies();
      tokenService.clearTokens();
    } catch (_err) {
      clearDriveState();
      try {
        await stytch?.session?.revoke?.();
      } catch {}
      clearStytchCookies();
      tokenService.clearTokens();
    }
  }, [clearDriveState, persistDriveRetryState, stytch]);

  // Check on mount if we're returning from a Drive connect redirect flow
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const driveCompleted = sessionStorage.getItem('drive_connect_completed');
    if (driveCompleted !== 'true') {
      return;
    }

    // Clear the flag immediately to prevent re-triggering
    sessionStorage.removeItem('drive_connect_completed');
    const completedState = sessionStorage.getItem('drive_connect_completed_state');
    sessionStorage.removeItem('drive_connect_completed_state');

    // Recover the pending token from before the redirect
    const storedToken = sessionStorage.getItem(DRIVE_CONNECT_PENDING_TOKEN_KEY);
    const storedSource = sessionStorage.getItem(DRIVE_CONNECT_PENDING_SOURCE_KEY);
    const storedState = sessionStorage.getItem(DRIVE_CONNECT_PENDING_STATE_KEY);
    const retryContext = buildDriveRetryContextFromStorage(storedToken, storedSource);
    sessionStorage.removeItem(DRIVE_CONNECT_PENDING_TOKEN_KEY);
    sessionStorage.removeItem(DRIVE_CONNECT_PENDING_SOURCE_KEY);
    sessionStorage.removeItem(DRIVE_CONNECT_PENDING_STATE_KEY);

    // Validate state matches if we have both
    if (storedState && completedState && storedState !== completedState) {
      console.warn('Drive connect state mismatch, ignoring completion');
      return;
    }

    if (retryContext) {
      // Restore the pending retry context and trigger retry
      pendingDriveRetryRef.current = retryContext;
      void handleDriveRetry();
    }
  }, [handleDriveRetry]);

  useEffect(() => {
    let cancelled = false;
    let activeRunId: number | null = null;

    const handleSessionChange = async () => {
      if (cancelled) {
        return;
      }

      // Skip the entire exchange flow on /oauth/authorize. Connected App
      // consent needs the Stytch session untouched — Sessions.Authenticate
      // rotates the opaque token and invalidates B2BIdentityProvider's cookies.
      // Checking pathname directly covers all cases: fresh load, already
      // logged in, and post-redirect with restored OAuth params.
      if (typeof window !== 'undefined' && window.location.pathname === '/oauth/authorize') {
        return;
      }

      // CRITICAL: Try to restore impersonation state FIRST, regardless of isInitialized.
      // This ensures impersonation persists across page refresh even when Stytch initializes quickly.
      if (!impersonationHydratedRef.current) {
        // Check if Stytch session cookie exists as a basic validity indicator.
        // This prevents restoring impersonation when admin's session was revoked/expired.
        // The cookie check is a defense-in-depth measure; server-side token validation
        // provides the ultimate security.
        const hasStytchSessionCookie = (() => {
          try {
            const match = document.cookie.match(/(?:^|; )stytch_session=([^;]+)/);
            return !!match;
          } catch {
            return false;
          }
        })();

        const wasActive = hydrateImpersonationIfActive(dispatch, hasStytchSessionCookie);
        if (wasActive) {
          impersonationHydratedRef.current = true;
          return; // Impersonation restored, don't continue with normal auth flow
        }
      }

      if (!isInitialized) {
        // No impersonation - try normal hydration
        const hydrated = await tokenService.hydrateFromStorage();
        if (!hydrated && !impersonationHydratedRef.current) {
          // No valid session AND not impersonating - clear any stale impersonation state
          tokenService.clearRuntimeImpersonation({ restoreAdminSession: false });
        }
        // If hydration failed and a silent cross-app exchange is still pending,
        // signal that auth resolution is in progress. This prevents
        // AuthenticatedRoute from redirecting to /login before the exchange
        // gets a chance to run once isInitialized becomes true.
        if (
          !hydrated &&
          !silentCrossAppExchangeAttemptedRef.current &&
          !exchangeInProgressRef.current
        ) {
          setIsExchanging(true);
        }
        return;
      }

      const sessionJWT = (session as any)?.session_jwt as string | undefined;
      const sessionTokenField = (session as any)?.session_token as string | undefined;
      const cookieSessionToken = (() => {
        try {
          const match = document.cookie.match(/(?:^|; )stytch_session=([^;]+)/);
          return match ? decodeURIComponent(match[1]) : '';
        } catch {
          return '';
        }
      })();

      const stytchSessionToken = sessionJWT || sessionTokenField || cookieSessionToken || '';
      const shouldAttemptCookieExchange = shouldAttemptCookieBasedExchange({
        stytchSessionToken,
        hasPlatformToken: !!token,
        authStatus: status,
        hasStytchSession: !!session,
      });

      if (stytchSessionToken || shouldAttemptCookieExchange) {
        const FAILURE_RETRY_DELAY_MS = 5_000;
        const now = Date.now();
        const { token: lastToken, outcome } = lastAttemptRef.current;
        const sameToken = lastToken === stytchSessionToken;
        if (sameToken) {
          if (outcome === 'failure') {
            if (now - lastAttemptRef.current.timestamp < FAILURE_RETRY_DELAY_MS) {
              return;
            }
            lastAttemptRef.current = {
              token: stytchSessionToken,
              outcome: null,
              timestamp: now,
            };
          } else {
            lastAttemptRef.current = {
              token: stytchSessionToken,
              outcome,
              timestamp: now,
            };
          }
        } else {
          lastAttemptRef.current = {
            token: stytchSessionToken,
            outcome: null,
            timestamp: now,
          };
        }

        const hasValidPlatformToken = token && status === 'authenticated';
        let platformTokenStillValid = false;
        if (hasValidPlatformToken) {
          try {
            const expiresAt = sessionStorage.getItem('platform_expires_at');
            if (expiresAt) {
              const expiryDate = new Date(expiresAt);
              const oneMinuteFromNow = new Date(Date.now() + 60 * 1000);
              platformTokenStillValid = expiryDate > oneMinuteFromNow;
            }
          } catch {
            platformTokenStillValid = false;
          }
        }

        if (platformTokenStillValid) {
          lastExchangedStytchTokenRef.current = stytchSessionToken;
          return;
        }

        if (cancelled || exchangeInProgressRef.current) {
          pendingRetryRef.current = true;
          return;
        }

        exchangeInProgressRef.current = true;
        pendingRetryRef.current = false;
        activeRunId = exchangeRunRef.current + 1;
        exchangeRunRef.current = activeRunId;
        setIsExchanging(true);

        try {
          const jwt = stytchSessionToken
            ? await tokenService.exchangeStytchSession(stytchSessionToken)
            : await tokenService.exchangeStytchSessionFromCookie();
          if (cancelled || activeRunId === null || exchangeRunRef.current !== activeRunId) {
            return;
          }

          if (jwt) {
            lastExchangedStytchTokenRef.current = stytchSessionToken;
            lastAttemptRef.current = {
              token: stytchSessionToken,
              outcome: 'success',
              timestamp: Date.now(),
            };
            clearDriveState();
            return;
          }

          const prompt = tokenService.consumeDrivePrompt();
          if (prompt?.authUrl) {
            persistDriveRetryState(
              buildDriveRetryContextFromSessionToken(stytchSessionToken),
              prompt.state
            );
            window.location.href = prompt.authUrl;
            return;
          }

          if (!stytchSessionToken) {
            lastAttemptRef.current = {
              token: stytchSessionToken,
              outcome: 'failure',
              timestamp: Date.now(),
            };
            return;
          }

          lastAttemptRef.current = {
            token: stytchSessionToken,
            outcome: 'failure',
            timestamp: Date.now(),
          };
          try {
            await stytch?.session?.revoke?.();
          } catch {}
          if (cancelled || activeRunId === null || exchangeRunRef.current !== activeRunId) {
            return;
          }
          clearStytchCookies();
          if (status === 'authenticated') {
            tokenService.clearTokens({
              expired: true,
              message: 'Authentication failed. Please sign in again.',
            });
          } else if (status === 'unauthenticated') {
            tokenService.clearTokens();
          }
          return;
        } catch (err) {
          if (err && typeof err === 'object' && 'authUrl' in err) {
            const driveErr = err as { authUrl: string; state?: string };
            if (driveErr.authUrl) {
              persistDriveRetryState(
                buildDriveRetryContextFromSessionToken(stytchSessionToken),
                driveErr.state
              );
              window.location.href = driveErr.authUrl;
              return;
            }
          }
          lastAttemptRef.current = {
            token: stytchSessionToken,
            outcome: 'failure',
            timestamp: Date.now(),
          };
          throw err;
        } finally {
          exchangeInProgressRef.current = false;
          if (!cancelled && activeRunId !== null && exchangeRunRef.current === activeRunId) {
            setIsExchanging(false);
            activeRunId = null;
            exchangeRunRef.current = 0;
            if (pendingRetryRef.current) {
              pendingRetryRef.current = false;
              void handleSessionChange();
            }
          }
        }
        return;
      }

      // If we're impersonating (ref is true), skip normal hydration - impersonation is already set up
      if (impersonationHydratedRef.current) {
        return;
      }

      const hydrated = await tokenService.hydrateFromStorage();
      if (cancelled) {
        return;
      }
      if (!hydrated) {
        // No stored Moments tokens. Try a silent cookie-based token exchange
        // for cross-app SSO (see shouldAttemptSilentCrossAppExchange docs).
        if (
          shouldAttemptSilentCrossAppExchange({
            isInitialized,
            alreadyAttempted: silentCrossAppExchangeAttemptedRef.current,
            exchangeInProgress: exchangeInProgressRef.current,
          })
        ) {
          silentCrossAppExchangeAttemptedRef.current = true;
          exchangeInProgressRef.current = true;
          // Register with exchangeRunRef so the effect cleanup function
          // can reset isExchanging if a dependency change triggers re-run
          // while this exchange is still in-flight.
          activeRunId = exchangeRunRef.current + 1;
          exchangeRunRef.current = activeRunId;
          setIsExchanging(true);
          try {
            const jwt = await tokenService.exchangeStytchSessionFromCookie();
            if (cancelled || activeRunId === null || exchangeRunRef.current !== activeRunId) {
              return;
            }
            if (jwt) {
              // Cross-app cookie exchange succeeded - user is now authenticated.
              clearDriveState();
              return;
            }
            // Check if Drive consent is required (428 from the backend).
            const prompt = tokenService.consumeDrivePrompt();
            if (prompt?.authUrl) {
              persistDriveRetryState({ source: 'cookie' }, prompt.state);
              window.location.href = prompt.authUrl;
              return;
            }
          } catch {
            // Silent failure - no valid cross-app session, fall through to login.
          } finally {
            exchangeInProgressRef.current = false;
            if (!cancelled && activeRunId !== null && exchangeRunRef.current === activeRunId) {
              setIsExchanging(false);
              activeRunId = null;
              exchangeRunRef.current = 0;
              if (pendingRetryRef.current) {
                pendingRetryRef.current = false;
                void handleSessionChange();
              }
            }
          }
        }

        // If another exchange is currently in progress, wait for it to complete
        // before clearing tokens - it may succeed and establish a session.
        if (exchangeInProgressRef.current) {
          return;
        }

        // No valid session - clear tokens
        if (status === 'authenticated') {
          tokenService.clearTokens({
            expired: true,
            message: 'Your session has ended. Please sign in again.',
          });
        } else if (status === 'unauthenticated') {
          tokenService.clearTokens();
        }
      }
    };

    void handleSessionChange();

    return () => {
      cancelled = true;
      if (exchangeRunRef.current !== 0) {
        setIsExchanging(false);
        exchangeRunRef.current = 0;
      }
    };
  }, [
    session,
    isInitialized,
    status,
    stytch,
    clearDriveState,
    persistDriveRetryState,
    token,
    dispatch,
  ]);

  useEffect(() => {
    if (!token) {
      if (isUserResolved) {
        dispatch(setUserResolved(false));
      }
      return;
    }

    if (
      shouldMarkUserResolved({
        token,
        isUserResolved,
        isMeUninitialized,
        isFetchingMe,
        isMeError,
        hasUserProfile: !!user,
      })
    ) {
      dispatch(setUserResolved(true));
      return;
    }

    if (
      shouldExpireUnresolvedSession({
        token,
        isUserResolved,
        isMeUninitialized,
        isFetchingMe,
        isMeError,
        hasUserProfile: !!user,
      })
    ) {
      tokenService.clearTokens({
        expired: true,
        message: 'We could not load your account. Please sign in again.',
      });
    }
  }, [token, isUserResolved, isMeUninitialized, isFetchingMe, isMeError, user, dispatch]);

  // When /users/me is available, persist the app user id in redux + session
  useEffect(() => {
    const appUser = meData?.user;
    if (!appUser) return;
    const rawId = appUser.id;
    if (rawId === null || rawId === undefined) {
      return;
    }

    const idAsString = typeof rawId === 'number' ? String(rawId) : String(rawId);
    const displayName = appUser.display_name || '';
    const [first, ...rest] = displayName.trim().split(/\s+/).filter(Boolean);
    const firstName = first ?? '';
    const lastName = rest.join(' ');

    // Use proxied avatar URL to avoid Google CDN rate limits
    const proxiedAvatarUrl = getProxiedAvatarUrl(idAsString, getStoredAccessToken());

    const identityShape = {
      id: idAsString,
      email: appUser.email,
      organization_id: appUser.org_id ?? '',
      organizationId: appUser.org_id ?? '',
      roles: Array.isArray((appUser as any).roles) ? (appUser as any).roles : [],
      permissions: Array.isArray((appUser as any).permissions) ? (appUser as any).permissions : [],
      permissions_hash: (appUser as any).permissions_hash,
      permissionsHash: (appUser as any).permissions_hash,
      given_name: firstName,
      family_name: lastName,
      avatarUrl: proxiedAvatarUrl,
      // Keep picture_url as fallback/reference (original Google URL)
      picture_url: (appUser as any).avatar_url || (appUser as any).picture_url || '',
    };
    try {
      if (typeof rawId === 'number' && Number.isFinite(rawId)) {
        sessionStorage.setItem('app_user_id', String(rawId));
      } else {
        sessionStorage.removeItem('app_user_id');
      }
      const serialized = JSON.stringify(identityShape);
      sessionStorage.setItem('platform_user', serialized);
      localStorage.setItem('platform_user', serialized);
    } catch {}

    if (typeof rawId === 'number' && Number.isFinite(rawId)) {
      dispatch(setAppUserId(rawId));
      // Keep /users/me cache synchronized after hydrating from Stytch
      dispatch(apiSlice.util.upsertQueryData('getMe', effectivePersonId, meData));
    } else {
      dispatch(setAppUserId(null));
    }

    dispatch(setUserProfile({ user: identityShape }));
  }, [meData, dispatch, effectivePersonId]);

  // Note: Impersonation state hydration is now handled inside handleSessionChange()
  // after hydrateFromStorage() completes to avoid race conditions

  // Feature flags come from the canonical identity-aware self-read query.
  const featureFlags = currentFeatureFlags.flags;
  const dismissGoogleMigrationPrompt = useCallback(() => {
    if (!googleMigrationPromptSignature || typeof window === 'undefined') {
      return;
    }

    sessionStorage.setItem(googleMigrationPromptDismissedKey, googleMigrationPromptSignature);
    setDismissedGoogleMigrationPrompt(googleMigrationPromptSignature);
  }, [googleMigrationPromptDismissedKey, googleMigrationPromptSignature]);
  const openGoogleIntegrationsSettings = useCallback(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.location.assign('/settings/integrations');
  }, []);

  return (
    <AuthContext.Provider value={{ isExchanging }}>
      <FeatureFlagProvider flags={featureFlags}>
        {showGoogleMigrationPrompt ? (
          <Box sx={{ px: 3, pt: 3 }} data-testid="google-migration-prompt">
            <Alert
              severity="warning"
              action={
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <Button size="small" color="inherit" onClick={openGoogleIntegrationsSettings}>
                    Review reconnects
                  </Button>
                  <Button size="small" color="inherit" onClick={dismissGoogleMigrationPrompt}>
                    Dismiss
                  </Button>
                </Box>
              }
            >
              Google reconnect required for{' '}
              {describeGoogleMigrationProviders(legacyGoogleProviders)}. Open Integrations to
              reconnect each affected Google integration and migrate it to the new Google OAuth
              client.
            </Alert>
          </Box>
        ) : null}
        {children}
      </FeatureFlagProvider>
    </AuthContext.Provider>
  );
}

// Outer wrapper that waits for Stytch to be ready
export function AuthProvider({ children }: AuthProviderProps) {
  const { ready } = useStytch();

  // If Stytch isn't ready yet, render children without auth logic
  // This allows the app to render while waiting for config.js
  if (!ready) {
    return <>{children}</>;
  }

  // Once Stytch is ready, render the inner component that uses hooks
  return <AuthProviderInner>{children}</AuthProviderInner>;
}
